import { BOT_MODEL_GROUPS, DEFAULT_BOT_MODEL } from "../shared/models";
import { formatChatTimestamp, sameCalendarDay } from "../shared/time";
import { DEFAULT_AVATARS, type ChatMessage, type PresenceUser, type ServerFrame } from "../shared/protocol";
import { linkifyHttps } from "../shared/sanitize";
import { api, publicSettings, session, type PublicSettings, type SessionPayload } from "./api";
import { connectRoom } from "./socket";
import { avatarImg, clear, h } from "./ui";

type AppState = {
  session: SessionPayload;
  settings: PublicSettings;
};

const root = document.getElementById("app")!;
let state: AppState | null = null;
let socketHandle: { send: (f: never) => void; close: () => void } | null = null;
let unreadTotal = 0;
let unreadTimer = 0;

function path(): string {
  const raw = location.pathname || "/";
  if (raw.length > 1 && raw.endsWith("/")) return raw.slice(0, -1);
  return raw;
}

function go(to: string) {
  const clean = to.length > 1 && to.endsWith("/") ? to.slice(0, -1) : to;
  history.pushState({}, "", clean);
  void render();
}

window.addEventListener("popstate", () => void render());

async function boot() {
  const [sess, settings] = await Promise.all([session().catch(() => ({ user: null, guest: null })), publicSettings()]);
  state = { session: sess, settings };
  document.title = settings.appName;
  const nameEl = document.getElementById("brand-name");
  if (nameEl) nameEl.textContent = settings.appName;
  await refreshUnread();
  if (unreadTimer) window.clearInterval(unreadTimer);
  unreadTimer = window.setInterval(() => void refreshUnread(), 15000);
  await render();
}

async function render() {
  if (!state) return;
  socketHandle?.close();
  socketHandle = null;
  const p = path();
  try {
    if (p === "/login") return await renderAuth("login");
    if (p === "/register") return await renderAuth("register");
    if (p === "/directory" || p === "/directories.html") return await renderDirectory();
    if (p === "/settings") return await renderSettings();
    if (p === "/messages") return await renderInbox();
    if (p.startsWith("/room/public")) return renderRoom("public");
    if (p.startsWith("/room/members")) return renderRoom("members");
    if (p.startsWith("/dm/")) return await renderDm(p.slice(4));
    if (p === "/admin" || p.startsWith("/admin/")) return await renderAdmin(p);
    return renderHome();
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    shell(h("main", { class: "page" },
      h("h1", {}, "Could not load this page"),
      h("p", { class: "error" }, msg),
      h("p", { class: "muted" }, "If you just deployed, try a hard refresh. Admin APIs still require an admin session."),
      buttonLink("/", "Back home"),
    ));
  }
}

function shell(main: HTMLElement, extraNav = true) {
  clear(root);
  const s = state!;
  const nav = h("header", { class: "topbar" },
    h("a", { class: "brand", href: "/" },
      s.settings.logoUrl ? h("img", { src: s.settings.logoUrl, class: "logo", alt: "" }) : null,
      h("span", { class: "brand-name" }, s.settings.appName),
    ),
    extraNav
      ? h("nav", { class: "nav" },
          link("/room/public", "Public"),
          link("/room/members", "Members"),
          link("/directory", "Directory"),
          s.session.user ? messagesLink() : null,
          s.session.user ? link("/settings", "Settings") : null,
          s.session.user?.role === "admin" ? link("/admin", "Admin") : null,
        )
      : null,
    h("div", { class: "session-chip" }, sessionChip()),
  );
  root.append(nav, main);
  nav.querySelector(".brand")?.addEventListener("click", (e) => {
    e.preventDefault();
    go("/");
  });
}

function messagesLink() {
  const label = unreadTotal > 0 ? `Messages (${unreadTotal > 99 ? "99+" : unreadTotal})` : "Messages";
  const a = link("/messages", label);
  if (unreadTotal > 0) a.classList.add("has-unread");
  return a;
}

async function refreshUnread() {
  if (!state?.session.user) {
    unreadTotal = 0;
    return;
  }
  try {
    const data = await api<{ unread: number }>("/api/dm/unread");
    unreadTotal = Number(data.unread || 0);
    const navLink = document.querySelector('.nav a[href="/messages"]');
    if (navLink) {
      navLink.textContent = unreadTotal > 0 ? `Messages (${unreadTotal > 99 ? "99+" : unreadTotal})` : "Messages";
      navLink.classList.toggle("has-unread", unreadTotal > 0);
    }
  } catch {
    /* stay signed-in even if badge fails */
  }
}

function link(href: string, label: string) {
  const a = h("a", { href, class: path().startsWith(href) ? "active" : "" }, label);
  a.addEventListener("click", (e) => {
    e.preventDefault();
    go(href);
  });
  return a;
}

function sessionChip() {
  const s = state!;
  if (s.session.user) {
    const wrap = h("div", { class: "who" },
      avatarImg(s.session.user.avatarUrl, s.session.user.displayName),
      h("span", {}, s.session.user.displayName),
    );
    const out = h("button", { class: "btn ghost", type: "button" }, "Sign out");
    out.addEventListener("click", async () => {
      await api("/api/auth/sign-out", { method: "POST", body: "{}" });
      state!.session = { user: null, guest: null };
      go("/");
    });
    return h("div", { class: "who-row" }, wrap, out);
  }
  if (s.session.guest) {
    return h("div", { class: "who" },
      avatarImg(`/avatars/${s.session.guest.avatarId}.svg`, s.session.guest.displayName),
      h("span", {}, `${s.session.guest.displayName} · guest`),
    );
  }
  return h("div", { class: "who-row" },
    buttonLink("/login", "Sign in"),
    buttonLink("/register", "Register", true),
  );
}

function buttonLink(href: string, label: string, primary = false) {
  const a = h("a", { href, class: primary ? "btn" : "btn ghost" }, label);
  a.addEventListener("click", (e) => {
    e.preventDefault();
    go(href);
  });
  return a;
}

function renderHome() {
  const s = state!;
  const main = h("main", { class: "landing" },
    h("section", { class: "hero" },
      h("p", { class: "eyebrow" }, s.settings.tagline),
      h("h1", {}, s.settings.appName),
      h("p", { class: "lede" }, "Public rooms, a members lounge, and private DMs — including Guides who sit in the same rooms as everyone else."),
      h("div", { class: "cta-row" },
        buttonLink("/room/public", "Enter Public Room", true),
        buttonLink("/directory", "Browse directory"),
      ),
    ),
    guestGate(),
  );
  shell(main);
}

function guestGate() {
  const s = state!;
  if (s.session.user || s.session.guest || !s.settings.guestEnabled) return h("div", {});
  const name = h("input", { class: "input", maxlength: "24", placeholder: "Pseudonym", required: "" }) as HTMLInputElement;
  const picker = h("div", { class: "avatar-picker" });
  let chosen = "01";
  for (const id of DEFAULT_AVATARS) {
    const btn = h("button", { type: "button", class: "avatar-opt", "data-id": id },
      h("img", { src: `/avatars/${id}.svg`, alt: id }),
    );
    btn.addEventListener("click", () => {
      chosen = id;
      picker.querySelectorAll(".avatar-opt").forEach((el) => el.classList.toggle("selected", el === btn));
    });
    if (id === "01") btn.classList.add("selected");
    picker.append(btn);
  }
  const submit = h("button", { class: "btn", type: "button" }, "Continue as guest");
  submit.addEventListener("click", async () => {
    try {
      const guest = await api<{ guest: SessionPayload["guest"] }>("/api/auth/guest", {
        method: "POST",
        body: JSON.stringify({ displayName: name.value, avatarId: chosen }),
      });
      state!.session.guest = guest.guest;
      go("/room/public");
    } catch (err) {
      alert((err as Error).message);
    }
  });
  return h("section", { class: "card guest-card" },
    h("h2", {}, "Join as a guest"),
    h("p", { class: "muted" }, "Guests can talk in Public. Members, DMs, and image uploads need an account."),
    name, picker, submit,
  );
}

function renderAuth(mode: "login" | "register") {
  const email = h("input", { class: "input", type: "email", placeholder: "Email", autocomplete: "email" }) as HTMLInputElement;
  const password = h("input", { class: "input", type: "password", placeholder: "Password", autocomplete: mode === "login" ? "current-password" : "new-password" }) as HTMLInputElement;
  const username = h("input", { class: "input", placeholder: "username", autocomplete: "username" }) as HTMLInputElement;
  const display = h("input", { class: "input", placeholder: "Display name" }) as HTMLInputElement;
  const err = h("p", { class: "error" });
  const submit = h("button", { class: "btn", type: "button" }, mode === "login" ? "Sign in" : "Create account");
  submit.addEventListener("click", async () => {
    err.textContent = "";
    try {
      if (mode === "login") {
        const res = await api<{ user: SessionPayload["user"] }>("/api/auth/login", {
          method: "POST",
          body: JSON.stringify({ email: email.value, password: password.value }),
        });
        state!.session.user = res.user;
        state!.session.guest = null;
      } else {
        const res = await api<{ user: SessionPayload["user"] }>("/api/auth/register", {
          method: "POST",
          body: JSON.stringify({
            email: email.value,
            password: password.value,
            username: username.value,
            displayName: display.value || username.value,
          }),
        });
        state!.session.user = res.user;
        state!.session.guest = null;
      }
      go("/room/members");
    } catch (e) {
      err.textContent = (e as Error).message;
    }
  });
  const main = h("main", { class: "auth-page" },
    h("section", { class: "card" },
      h("h1", {}, mode === "login" ? "Sign in" : "Create an account"),
      email,
      mode === "register" ? username : null,
      mode === "register" ? display : null,
      password,
      err,
      submit,
      h("p", { class: "muted oauth" },
        h("a", { href: "/api/auth/oauth/github" }, "Continue with GitHub"),
        " · ",
        h("a", { href: "/api/auth/oauth/google" }, "Continue with Google"),
      ),
      h("p", { class: "muted" },
        mode === "login" ? "Need an account? " : "Already registered? ",
        buttonLink(mode === "login" ? "/register" : "/login", mode === "login" ? "Register" : "Sign in"),
      ),
    ),
  );
  shell(main);
}

async function renderDirectory() {
  shell(h("main", { class: "page" }, h("h1", {}, "Directory"), h("p", { class: "muted" }, "Loading…")));
  const data = await api<{
    signedIn: boolean;
    humans?: DirRow[];
    bots?: (DirRow & { mark: string; dm_enabled?: number })[];
  }>("/api/directory");
  const list = h("div", { class: "dir-grid" });
  const all = [...(data.bots || []), ...(data.humans || [])];
  for (const row of all) {
    const card = h("article", { class: "dir-card" },
      avatarImg(row.avatar_url, row.display_name),
      h("div", {},
        h("h3", {}, row.display_name, row.kind === "bot" ? h("span", { class: "pill bot" }, "Guide") : null),
        h("p", { class: "muted" }, `@${row.username}`),
        h("p", {}, row.bio || ""),
      ),
    );
    const btn = h("button", { class: "btn ghost", type: "button" }, "Message");
    btn.addEventListener("click", async () => {
      if (!state!.session.user) {
        go("/login");
        return;
      }
      try {
        await api("/api/dm/open", { method: "POST", body: JSON.stringify({ username: row.username }) });
        go(`/dm/${row.username}`);
      } catch (e) {
        alert((e as Error).message);
      }
    });
    card.append(btn);
    list.append(card);
  }
  shell(h("main", { class: "page" }, h("h1", {}, "Directory"), list));
}

type DirRow = {
  id: string;
  kind: string;
  username: string;
  display_name: string;
  avatar_url: string;
  bio: string;
};

function renderRoom(kind: "public" | "members") {
  if (kind === "members" && !state!.session.user) {
    shell(h("main", { class: "page" },
      h("h1", {}, "Members Room"),
      h("p", {}, "Registered accounts only."),
      buttonLink("/login", "Sign in", true),
    ));
    return;
  }
  if (kind === "public" && !state!.session.user && !state!.session.guest) {
    shell(h("main", { class: "page" },
      h("h1", {}, "Public Room"),
      h("p", {}, "Pick a guest name first, or sign in."),
      guestGate(),
    ));
    return;
  }
  mountChat({
    title: kind === "public" ? "Public Room" : "Members Room",
    welcome: kind === "public" ? state!.settings.welcomePublic : state!.settings.welcomeMembers,
    wsPath: `/room/${kind}`,
    allowImages: false,
    watermark: !state!.session.user,
  });
}

async function renderDm(username: string) {
  if (!state!.session.user) {
    shell(h("main", { class: "page" }, h("h1", {}, "Direct messages"), h("p", {}, "Sign in to send private messages."), buttonLink("/login", "Sign in", true)));
    return;
  }
  try {
    const opened = await api<{
      threadId: string;
      peer: { id: string; username: string; displayName: string; avatarUrl: string; kind: string };
    }>("/api/dm/open", { method: "POST", body: JSON.stringify({ username }) });
    mountChat({
      title: opened.peer.displayName,
      subtitle: `@${opened.peer.username}${opened.peer.kind === "bot" ? " · Guide" : ""}`,
      welcome: "",
      wsPath: `/dm/${opened.threadId}`,
      allowImages: true,
      peerId: opened.peer.id,
      watermark: false,
    });
    void api("/api/dm/read", { method: "POST", body: JSON.stringify({ threadId: opened.threadId }) })
      .then(() => refreshUnread())
      .catch(() => {});
  } catch (e) {
    shell(h("main", { class: "page" }, h("h1", {}, "Unable to open DM"), h("p", {}, (e as Error).message)));
  }
}

async function renderInbox() {
  if (!state!.session.user) return go("/login");
  shell(h("main", { class: "page" }, h("h1", {}, "Messages"), h("p", { class: "muted" }, "Loading…")));
  const data = await api<{
    threads?: {
      threadId: string;
      lastPreview: string | null;
      lastMessageAt?: string | null;
      unread?: number;
      peer: { username: string; display_name: string; avatar_url: string; kind: string } | null;
    }[];
    unread?: number;
  }>("/api/dm/threads");
  unreadTotal = Number(data.unread || 0);
  const list = h("div", { class: "inbox" });
  const threads = data.threads || [];
  for (const t of threads) {
    if (!t.peer) continue;
    const unread = Number(t.unread || 0);
    const preview = t.lastPreview || "No messages yet";
    const row = h("a", { class: unread ? "inbox-row unread" : "inbox-row", href: `/dm/${t.peer.username}` },
      avatarImg(t.peer.avatar_url, t.peer.display_name),
      h("div", { class: "inbox-copy" },
        h("strong", {}, t.peer.display_name, t.peer.kind === "bot" ? h("span", { class: "pill bot" }, "Guide") : null),
        h("p", { class: "muted" }, preview),
      ),
      unread > 0 ? h("span", { class: "unread-dot", title: `${unread} new` }, unread > 9 ? "9+" : String(unread)) : null,
    );
    row.addEventListener("click", (e) => {
      e.preventDefault();
      go(`/dm/${t.peer!.username}`);
    });
    list.append(row);
  }
  if (!threads.length) list.append(h("p", { class: "muted" }, "No conversations yet. Open someone from the directory."));
  shell(h("main", { class: "page" }, h("h1", {}, "Messages"), list));
}

function mountChat(opts: {
  title: string;
  subtitle?: string;
  welcome: string;
  wsPath: string;
  allowImages: boolean;
  peerId?: string;
  watermark: boolean;
}) {
  const log = h("div", { class: "log" });
  const presence = h("aside", { class: "presence" });
  const typingEl = h("div", { class: "typing" });
  const banner = h("div", { class: "reconnect hidden" }, "Reconnecting…");
  const composer = h("form", { class: "composer" });
  const input = h("textarea", {
    class: "composer-input",
    rows: "1",
    maxlength: String(state!.settings.maxMessageLength),
    placeholder: "Message",
  }) as HTMLTextAreaElement;
  const sendBtn = h("button", { class: "btn", type: "submit" }, "Send");
  const imgBtn = opts.allowImages
    ? (h("label", { class: "btn ghost file-btn" }, "Photo", h("input", { type: "file", accept: "image/*", hidden: "" })) as HTMLLabelElement)
    : null;
  composer.append(input, imgBtn ?? document.createTextNode(""), sendBtn);

  const main = h("main", { class: "chat-layout" },
    h("section", { class: "chat-col" },
      h("div", { class: "chat-head" },
        h("div", {},
          h("h1", {}, opts.title),
          opts.subtitle ? h("p", { class: "muted" }, opts.subtitle) : null,
        ),
        banner,
      ),
      opts.watermark ? h("div", { class: "watermark" }, "Guest · Public only") : null,
      log,
      typingEl,
      composer,
    ),
    presence,
  );
  shell(main);

  const messages: ChatMessage[] = [];
  const pending = new Map<string, HTMLElement>();
  const me = state!.session.user?.id || state!.session.guest?.guestId || "";

  const renderLog = () => {
    clear(log);
    let lastDay = "";
    for (const m of messages) {
      if (!lastDay || !sameCalendarDay(lastDay, m.createdAt)) {
        log.append(h("div", { class: "day-sep" }, formatChatTimestamp(m.createdAt).replace(/ at .+$/, "")));
        lastDay = m.createdAt;
      }
      log.append(bubble(m, me));
    }
    log.scrollTop = log.scrollHeight;
  };

  if (opts.welcome) log.append(h("div", { class: "system" }, opts.welcome));

  const sock = connectRoom(opts.wsPath, {
    onStatus(st) {
      banner.classList.toggle("hidden", st === "open");
      banner.textContent = st === "open" ? "" : "Reconnecting…";
    },
    onFrame(frame: ServerFrame) {
      if (frame.type === "hello") {
        messages.splice(0, messages.length, ...frame.history);
        renderLog();
      } else if (frame.type === "message") {
        const existing = messages.findIndex((m) => m.id === frame.message.id);
        if (existing >= 0) messages[existing] = frame.message;
        else messages.push(frame.message);
        pending.get(frame.message.id)?.remove();
        pending.delete(frame.message.id);
        renderLog();
      } else if (frame.type === "presence") {
        drawPresence(presence, frame.users);
      } else if (frame.type === "typing") {
        typingEl.textContent = frame.isTyping ? `${frame.displayName} is typing…` : "";
      } else if (frame.type === "system") {
        log.append(h("div", { class: "system" }, frame.body));
      } else if (frame.type === "error") {
        typingEl.textContent = frame.message;
      } else if (frame.type === "moderation") {
        log.append(h("div", { class: "system" }, frame.action));
      }
    },
  });
  socketHandle = sock as never;

  let typing = false;
  let typingTimer = 0;
  input.addEventListener("input", () => {
    if (!typing) {
      typing = true;
      sock.send({ type: "typing", isTyping: true });
    }
    window.clearTimeout(typingTimer);
    typingTimer = window.setTimeout(() => {
      typing = false;
      sock.send({ type: "typing", isTyping: false });
    }, 1400);
  });

  composer.addEventListener("submit", (e) => {
    e.preventDefault();
    const body = input.value.trim();
    if (!body) return;
    const clientMsgId = crypto.randomUUID();
    const optimistic: ChatMessage = {
      id: clientMsgId,
      roomId: opts.wsPath,
      senderId: me,
      senderKind: state!.session.user ? "account" : "guest",
      displayName: state!.session.user?.displayName || state!.session.guest?.displayName || "You",
      avatarUrl: state!.session.user?.avatarUrl || `/avatars/${state!.session.guest?.avatarId || "01"}.svg`,
      body,
      createdAt: new Date().toISOString(),
      clientMsgId,
    };
    messages.push(optimistic);
    renderLog();
    sock.send({ type: "message", clientMsgId, body });
    input.value = "";
    sock.send({ type: "typing", isTyping: false });
  });

  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      composer.requestSubmit();
    }
  });

  imgBtn?.querySelector("input")?.addEventListener("change", async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file || !opts.peerId) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("peerId", opts.peerId);
    const res = await fetch("/api/dm/images", { method: "POST", body: fd, credentials: "include" });
    if (!res.ok) {
      alert("Image upload failed");
      return;
    }
    const { imageKey } = (await res.json()) as { imageKey: string };
    sock.send({ type: "message", clientMsgId: crypto.randomUUID(), body: "", imageKey });
  });
}

function bubble(m: ChatMessage, me: string) {
  const mine = m.senderId === me;
  const body = h("div", { class: "bubble-body" });
  if (m.imageUrl) body.append(h("img", { class: "chat-img", src: m.imageUrl, alt: "" }));
  if (m.body) {
    const p = h("p");
    p.innerHTML = linkifyHttps(m.body);
    body.append(p);
  }
  return h("article", { class: `msg ${mine ? "own" : "other"}` },
    avatarImg(m.avatarUrl, m.displayName),
    h("div", {},
      h("header", { class: "msg-meta" },
        h("strong", {}, m.displayName),
        m.senderKind === "bot" ? h("span", { class: "pill bot" }, "Guide") : null,
        m.senderKind === "guest" ? h("span", { class: "pill guest" }, "Guest") : null,
        h("time", {}, formatChatTimestamp(m.createdAt)),
      ),
      body,
    ),
  );
}

function drawPresence(el: HTMLElement, users: PresenceUser[]) {
  clear(el);
  el.append(h("h2", {}, "In the room"));
  for (const u of users) {
    el.append(
      h("div", { class: "presence-row" },
        avatarImg(u.avatarUrl, u.displayName),
        h("span", {}, u.displayName),
        u.kind === "bot" ? h("span", { class: "pill bot" }, "Guide") : null,
        u.kind === "guest" ? h("span", { class: "pill guest" }, "Guest") : null,
        u.role === "admin" ? h("span", { class: "pill admin" }, "Admin") : null,
      ),
    );
  }
}

async function renderSettings() {
  if (!state!.session.user) return go("/login");
  const user = state!.session.user;
  const name = h("input", { class: "input", value: user.displayName }) as HTMLInputElement;
  const bio = h("textarea", { class: "input", rows: "3" }, user.bio) as HTMLTextAreaElement;
  const save = h("button", { class: "btn", type: "button" }, "Save");
  save.addEventListener("click", async () => {
    const res = await api<{ user: SessionPayload["user"] }>("/api/me", {
      method: "PATCH",
      body: JSON.stringify({ displayName: name.value, bio: bio.value }),
    });
    state!.session.user = res.user;
    alert("Saved");
  });
  const file = h("input", { type: "file", accept: "image/*" }) as HTMLInputElement;
  file.addEventListener("change", async () => {
    if (!file.files?.[0]) return;
    const fd = new FormData();
    fd.append("file", file.files[0]);
    const res = await fetch("/api/me/avatar", { method: "POST", body: fd, credentials: "include" });
    const json = (await res.json()) as { avatarUrl?: string };
    if (json.avatarUrl && state!.session.user) state!.session.user.avatarUrl = json.avatarUrl;
    void render();
  });
  shell(h("main", { class: "page card-page" },
    h("h1", {}, "Settings"),
    avatarImg(user.avatarUrl, user.displayName),
    file,
    name,
    bio,
    save,
  ));
}

async function renderAdmin(p: string) {
  if (state!.session.user?.role !== "admin") {
    shell(h("main", { class: "page" }, h("h1", {}, "Not found")));
    return;
  }
  if (p === "/admin/bots/new") return renderBotForm(null);
  const preview = p.match(/^\/admin\/bots\/([^/]+)\/preview$/);
  if (preview) return renderBotPreview(preview[1]);
  const control = p.match(/^\/admin\/bots\/([^/]+)\/control$/);
  if (control) return renderBotControl(control[1]);
  const edit = p.match(/^\/admin\/bots\/([^/]+)$/);
  if (edit && edit[1] !== "new") return renderBotForm(edit[1]);
  if (p.startsWith("/admin/bots")) return renderBotList();
  return renderAdminHome();
}

function modelSelect(current: string): HTMLSelectElement {
  const sel = h("select", { class: "input" }) as HTMLSelectElement;
  const known = new Set<string>();
  for (const group of BOT_MODEL_GROUPS) {
    const og = document.createElement("optgroup");
    og.label = group.label;
    for (const model of group.models) {
      known.add(model.id);
      const opt = document.createElement("option");
      opt.value = model.id;
      opt.textContent = model.hint ? `${model.label} — ${model.hint}` : model.label;
      og.append(opt);
    }
    sel.append(og);
  }
  const value = current.trim() || DEFAULT_BOT_MODEL;
  if (value && !known.has(value)) {
    const extra = document.createElement("option");
    extra.value = value;
    extra.textContent = `${value} (currently saved)`;
    sel.insertBefore(extra, sel.firstChild);
  }
  sel.value = value;
  return sel;
}

async function renderAdminHome() {
  const settings = await api<Record<string, unknown>>("/api/admin/settings");
  const users = await api<{ users: Record<string, string>[] }>("/api/admin/users");
  const form = h("form", { class: "stack" });
  const fields: Record<string, HTMLInputElement | HTMLSelectElement> = {};
  for (const [label, key] of [
    ["App name", "appName"],
    ["Tagline", "tagline"],
    ["Accent", "accent"],
    ["AI Gateway id", "aiGatewayId"],
  ] as const) {
    const input = h("input", { class: "input", value: String((key === "accent" ? (settings.theme as Record<string, string>)?.accent : settings[key]) ?? "") }) as HTMLInputElement;
    fields[key] = input;
    form.append(h("label", { class: "stack" }, label, input));
  }
  const defaultModel = modelSelect(String(settings.defaultBotModel || DEFAULT_BOT_MODEL));
  fields.defaultBotModel = defaultModel;
  form.append(h("label", { class: "stack" }, "Default bot model", defaultModel));
  const save = h("button", { class: "btn", type: "button" }, "Save branding");
  save.addEventListener("click", async () => {
    await api("/api/admin/settings", {
      method: "PUT",
      body: JSON.stringify({
        appName: fields.appName.value,
        tagline: fields.tagline.value,
        aiGatewayId: fields.aiGatewayId.value || null,
        defaultBotModel: fields.defaultBotModel.value,
        theme: { accent: fields.accent.value },
      }),
    });
    const link = document.getElementById("theme-css") as HTMLLinkElement | null;
    if (link) link.href = "/api/theme.css?ts=" + Date.now();
    alert("Saved");
  });
  const dark = h("button", { class: "btn ghost", type: "button" }, "Dark preset");
  const light = h("button", { class: "btn ghost", type: "button" }, "Light preset");
  dark.onclick = async () => {
    await api("/api/admin/settings", { method: "PUT", body: JSON.stringify({ preset: "dark" }) });
    location.reload();
  };
  light.onclick = async () => {
    await api("/api/admin/settings", { method: "PUT", body: JSON.stringify({ preset: "light" }) });
    location.reload();
  };
  form.append(save, dark, light);

  const table = h("table", { class: "table" });
  table.append(h("thead", {}, h("tr", {}, h("th", {}, "User"), h("th", {}, "Status"), h("th", {}, "Actions"))));
  const tbody = h("tbody");
  for (const u of users.users) {
    const actions = h("td");
    for (const action of ["suspend", "ban", "resume", "delete"]) {
      const b = h("button", { class: "btn ghost tiny", type: "button" }, action);
      b.addEventListener("click", async () => {
        const reason = prompt("Reason") || "";
        await api(`/api/admin/users/${u.id}/moderate`, { method: "POST", body: JSON.stringify({ action, reason }) });
        void render();
      });
      actions.append(b);
    }
    tbody.append(h("tr", {},
      h("td", {}, `${u.display_name} @${u.username}`),
      h("td", {}, u.status),
      actions,
    ));
  }
  table.append(tbody);

  shell(h("main", { class: "page" },
    h("h1", {}, "Admin"),
    h("nav", { class: "subnav" }, link("/admin", "Branding"), link("/admin/bots", "Bots")),
    h("h2", {}, "Branding"),
    form,
    h("h2", {}, "Human accounts"),
    table,
  ));
}

async function renderBotList() {
  const data = await api<{
    bots: {
      id: string;
      username: string;
      display_name: string;
      avatar_url: string;
      dispatch_public: number;
      dispatch_members: number;
      dm_enabled: number;
      hidden: number;
      control_mode?: string;
    }[];
  }>("/api/admin/bots");
  const list = h("div", { class: "stack" });
  for (const b of data.bots) {
    const row = h("article", { class: "dir-card" },
      avatarImg(b.avatar_url, b.display_name),
      h("div", {},
        h("h3", {}, b.display_name, h("span", { class: "muted" }, ` @${b.username}`)),
        h("p", {},
          b.dispatch_public ? h("span", { class: "chip" }, "Public") : null,
          b.dispatch_members ? h("span", { class: "chip" }, "Members") : null,
          b.dm_enabled ? h("span", { class: "chip" }, "DM") : null,
          b.hidden ? h("span", { class: "chip" }, "Hidden") : null,
          h("span", { class: "chip" }, b.control_mode === "manual" ? "Manual" : "Auto"),
        ),
      ),
    );
    const edit = buttonLink(`/admin/bots/${b.id}`, "Edit");
    const preview = buttonLink(`/admin/bots/${b.id}/preview`, "Preview");
    const control = buttonLink(`/admin/bots/${b.id}/control`, "Take control");
    const hide = h("button", { class: "btn ghost", type: "button" }, "Hide & pull");
    hide.onclick = async () => {
      await api(`/api/admin/bots/${b.id}/hide`, { method: "POST", body: JSON.stringify({ pull: true }) });
      void render();
    };
    const del = h("button", { class: "btn danger ghost", type: "button" }, "Delete");
    del.onclick = async () => {
      if (!confirm("Delete this bot?")) return;
      await api(`/api/admin/bots/${b.id}/delete`, { method: "POST", body: "{}" });
      void render();
    };
    row.append(h("div", { class: "btn-row" }, edit, preview, control, hide, del));
    list.append(row);
  }
  shell(h("main", { class: "page" },
    h("h1", {}, "Bots"),
    buttonLink("/admin/bots/new", "Create bot", true),
    list,
  ));
}

async function renderBotForm(id: string | null) {
  let existing: {
    user: Record<string, string>;
    profile: Record<string, unknown>;
    files: { id: string; filename: string; bytes: number }[];
    debug?: { lastError: string; lastModel: string; controlMode: string };
  } | null = null;
  if (id) existing = await api(`/api/admin/bots/${id}`);
  const f = {
    username: h("input", { class: "input", value: existing?.user.username || "", disabled: id ? "" : undefined }) as HTMLInputElement,
    displayName: h("input", { class: "input", value: existing?.user.display_name || "" }) as HTMLInputElement,
    bio: h("textarea", { class: "input", rows: "2" }, existing?.user.bio || "") as HTMLTextAreaElement,
    personality: h("textarea", { class: "input", rows: "10", placeholder: "Character bible (200–8000 chars)" }, String(existing?.profile.personality || "")) as HTMLTextAreaElement,
    greeting: h("input", { class: "input", value: String(existing?.profile.greeting || "") }) as HTMLInputElement,
    modelId: modelSelect(String(existing?.profile.model_id || DEFAULT_BOT_MODEL)),
    temperature: h("input", { class: "input", type: "number", step: "0.1", value: String(existing?.profile.temperature ?? 0.8) }) as HTMLInputElement,
    maxTokens: h("input", { class: "input", type: "number", value: String(existing?.profile.max_tokens ?? 220) }) as HTMLInputElement,
    dispatchPublic: h("input", { type: "checkbox" }) as HTMLInputElement,
    dispatchMembers: h("input", { type: "checkbox" }) as HTMLInputElement,
    dmEnabled: h("input", { type: "checkbox" }) as HTMLInputElement,
    hidden: h("input", { type: "checkbox" }) as HTMLInputElement,
    manual: h("input", { type: "checkbox" }) as HTMLInputElement,
  };
  f.dispatchPublic.checked = existing?.profile.dispatch_public === 1;
  f.dispatchMembers.checked = existing?.profile.dispatch_members === 1;
  f.dmEnabled.checked = existing ? existing.profile.dm_enabled === 1 : true;
  f.hidden.checked = existing?.profile.hidden === 1;
  f.manual.checked = existing?.profile.control_mode === "manual";

  const save = h("button", { class: "btn", type: "button" }, id ? "Save bot" : "Create bot");
  save.onclick = async () => {
    const payload = {
      username: f.username.value,
      displayName: f.displayName.value,
      bio: f.bio.value,
      personality: f.personality.value,
      greeting: f.greeting.value,
      modelId: f.modelId.value,
      temperature: Number(f.temperature.value),
      maxTokens: Number(f.maxTokens.value),
      dispatchPublic: f.dispatchPublic.checked,
      dispatchMembers: f.dispatchMembers.checked,
      dmEnabled: f.dmEnabled.checked,
      hidden: f.hidden.checked,
      controlMode: f.manual.checked ? "manual" : "auto",
    };
    if (id) await api(`/api/admin/bots/${id}`, { method: "PUT", body: JSON.stringify(payload) });
    else {
      const created = await api<{ id: string }>(`/api/admin/bots`, { method: "POST", body: JSON.stringify(payload) });
      go(`/admin/bots/${created.id}`);
      return;
    }
    alert("Saved");
  };

  const kb = h("div", { class: "stack" });
  if (id && existing) {
    for (const file of existing.files) {
      const row = h("div", { class: "inbox-row" }, h("span", {}, `${file.filename} (${file.bytes} bytes)`));
      const del = h("button", { class: "btn ghost tiny", type: "button" }, "Delete");
      del.onclick = async () => {
        await fetch(`/api/admin/bots/${id}/kb/${file.id}`, { method: "DELETE", credentials: "include" });
        void render();
      };
      row.append(del);
      kb.append(row);
    }
    const up = h("input", { type: "file", accept: ".txt,.md,.pdf,.html,.csv" }) as HTMLInputElement;
    up.onchange = async () => {
      if (!up.files?.[0]) return;
      const fd = new FormData();
      fd.append("file", up.files[0]);
      await fetch(`/api/admin/bots/${id}/kb`, { method: "POST", body: fd, credentials: "include" });
      void render();
    };
    const reindex = h("button", { class: "btn ghost", type: "button" }, "Reindex");
    reindex.onclick = async () => {
      await api(`/api/admin/bots/${id}/reindex`, { method: "POST", body: "{}" });
      alert("Reindexed");
    };
    const av = h("input", { type: "file", accept: "image/*" }) as HTMLInputElement;
    av.onchange = async () => {
      if (!av.files?.[0]) return;
      const fd = new FormData();
      fd.append("file", av.files[0]);
      await fetch(`/api/admin/bots/${id}/avatar`, { method: "POST", body: fd, credentials: "include" });
      void render();
    };
    kb.append(h("p", {}, "Avatar"), av, h("p", {}, "Knowledge files"), up, reindex);
  }

  shell(h("main", { class: "page card-page" },
    h("h1", {}, id ? "Edit bot" : "New bot"),
    labeled("Username", f.username),
    labeled("Display name", f.displayName),
    labeled("Bio", f.bio),
    labeled("Personality", f.personality),
    labeled("Greeting", f.greeting),
    labeled("Model", f.modelId),
    labeled("Temperature", f.temperature),
    labeled("Max tokens", f.maxTokens),
    h("label", { class: "check" }, f.dispatchPublic, " Dispatch Public"),
    h("label", { class: "check" }, f.dispatchMembers, " Dispatch Members"),
    h("label", { class: "check" }, f.dmEnabled, " Allow DMs"),
    h("label", { class: "check" }, f.hidden, " Hide from directory"),
    h("label", { class: "check" }, f.manual, " Take control (disable auto-replies; you speak as this bot)"),
    existing?.debug?.lastError
      ? h("p", { class: "error" }, `Last AI error: ${existing.debug.lastError}`)
      : existing?.debug?.lastModel
        ? h("p", { class: "muted" }, `Last model used: ${existing.debug.lastModel}`)
        : null,
    id ? buttonLink(`/admin/bots/${id}/control`, "Open control desk") : null,
    save,
    kb,
  ));
}

function labeled(label: string, el: HTMLElement) {
  return h("label", { class: "stack" }, label, el);
}

async function renderBotPreview(id: string) {
  const meta = await api<{ user: { display_name: string }; debug?: { lastError: string; lastModel: string } }>(`/api/admin/bots/${id}`);
  const log = h("div", { class: "log preview-log" });
  const status = h("p", { class: "muted" }, meta.debug?.lastError ? `Last AI error: ${meta.debug.lastError}` : "");
  const input = h("textarea", { class: "composer-input", rows: "2", placeholder: "Talk to the bot" }) as HTMLTextAreaElement;
  const history: { role: string; content: string }[] = [];
  const send = h("button", { class: "btn", type: "button" }, "Send");
  send.onclick = async () => {
    const message = input.value.trim();
    if (!message) return;
    input.value = "";
    log.append(h("div", { class: "msg own" }, h("p", {}, message)));
    history.push({ role: "user", content: message });
    const res = await api<{ reply: string; error?: string; model?: string }>(`/api/admin/bots/${id}/preview`, {
      method: "POST",
      body: JSON.stringify({ message, history }),
    });
    history.push({ role: "assistant", content: res.reply });
    log.append(h("div", { class: "msg other" }, h("p", {}, res.reply)));
    status.textContent = res.error ? `AI error: ${res.error}` : res.model ? `Model: ${res.model}` : "";
    log.scrollTop = log.scrollHeight;
  };
  shell(h("main", { class: "page" },
    h("h1", {}, `Preview · ${meta.user.display_name}`),
    status,
    log,
    h("div", { class: "composer" }, input, send),
  ));
}

async function renderBotControl(id: string) {
  const meta = await api<{
    user: { display_name: string; username: string; avatar_url: string };
    profile: { control_mode?: string; dispatch_public?: number; dispatch_members?: number };
    debug?: { lastError: string; lastModel: string; controlMode: string };
  }>(`/api/admin/bots/${id}`);
  const mode = meta.profile.control_mode === "manual" ? "manual" : "auto";
  const banner = h("p", { class: "muted" },
    mode === "manual"
      ? "You are speaking as this bot. Auto-replies are off. Switch back to Auto when you want the model to answer."
      : "Auto-respond is on. Take control to type (and send photos) as this bot.",
  );
  const toggle = h("button", { class: "btn", type: "button" }, mode === "manual" ? "Return to auto-respond" : "Take control");
  toggle.onclick = async () => {
    await api(`/api/admin/bots/${id}/control`, {
      method: "POST",
      body: JSON.stringify({ mode: mode === "manual" ? "auto" : "manual" }),
    });
    void render();
  };

  const target = h("select", { class: "input" },
    h("option", { value: "public" }, "Public Room"),
    h("option", { value: "members" }, "Members Room"),
  ) as HTMLSelectElement;
  const dmUser = h("input", { class: "input", placeholder: "Or DM username (without @)" }) as HTMLInputElement;
  const text = h("textarea", { class: "composer-input", rows: "3", placeholder: `Message as ${meta.user.display_name}` }) as HTMLTextAreaElement;
  const file = h("input", { type: "file", accept: "image/*" }) as HTMLInputElement;
  const send = h("button", { class: "btn", type: "button" }, "Send as bot");
  const note = h("p", { class: "muted" });

  send.onclick = async () => {
    note.textContent = "";
    if (mode !== "manual") {
      note.textContent = "Take control first — otherwise the bot stays on auto-respond.";
      return;
    }
    let imageKey = "";
    let imageUrl = "";
    const dest = dmUser.value.trim() || target.value;
    if (file.files?.[0]) {
      const fd = new FormData();
      fd.append("file", file.files[0]);
      const res = await fetch(`/api/admin/bots/${id}/images`, { method: "POST", body: fd, credentials: "include" });
      const json = (await res.json()) as { imageKey?: string; imageUrl?: string; error?: string };
      if (!res.ok || !json.imageKey) {
        note.textContent = json.error || "Image upload failed";
        return;
      }
      imageKey = json.imageKey;
      imageUrl = json.imageUrl || "";
    }
    if (!text.value.trim() && !imageKey) {
      note.textContent = "Type a message or attach a photo.";
      return;
    }
    const spoken = await api<{ ok: boolean; error?: string; roomId?: string }>(`/api/admin/bots/${id}/speak`, {
      method: "POST",
      body: JSON.stringify({ target: dest, text: text.value, imageKey, imageUrl }),
    });
    if (!spoken.ok) {
      note.textContent = spoken.error || "Send failed";
      return;
    }
    text.value = "";
    file.value = "";
    note.textContent = `Sent as @${meta.user.username} → ${spoken.roomId}`;
  };

  shell(h("main", { class: "page card-page" },
    h("h1", {}, `Control · ${meta.user.display_name}`),
    banner,
    toggle,
    meta.debug?.lastError ? h("p", { class: "error" }, `Last AI error: ${meta.debug.lastError}`) : null,
    h("h2", {}, "Speak as this bot"),
    labeled("Room", target),
    labeled("Direct message to", dmUser),
    text,
    h("label", { class: "stack" }, "Photo (optional)", file),
    send,
    note,
    buttonLink(`/admin/bots/${id}`, "Back to edit"),
  ));
}

void boot();
